import type { Recommendation } from '../types/health';

interface RecommendationsProps {
  recommendations: Recommendation[];
}

export default function Recommendations({ recommendations }: RecommendationsProps) {
  const priorityColors = {
    high: 'border-red-200 bg-red-50',
    medium: 'border-orange-200 bg-orange-50',
    low: 'border-blue-200 bg-blue-50'
  };

  const priorityIcons = {
    high: 'ri-alert-line text-red-600',
    medium: 'ri-error-warning-line text-orange-600',
    low: 'ri-information-line text-blue-600'
  };

  if (recommendations.length === 0) {
    return (
      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
          <i className="ri-trophy-line text-3xl text-green-600"></i>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Excellent Work!</h3>
        <p className="text-gray-700">You're doing great! Keep up your healthy habits to maintain your score.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">Personalized Recommendations</h3>
        <span className="text-sm text-gray-600">{recommendations.length} suggestions</span>
      </div>

      <div className="space-y-4">
        {recommendations.map((rec, index) => (
          <div
            key={index}
            className={`border-2 rounded-xl p-6 ${priorityColors[rec.priority]}`}
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                <i className={`text-xl ${priorityIcons[rec.priority]}`}></i>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs font-semibold text-gray-600 uppercase">{rec.category}</span>
                  <span className="px-2 py-1 bg-white rounded-full text-xs font-semibold text-gray-700">
                    Priority: {rec.priority}
                  </span>
                  <span className="px-2 py-1 bg-white rounded-full text-xs font-semibold text-teal-700">
                    +{rec.potentialImpact} points
                  </span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{rec.title}</h4>
                <p className="text-sm text-gray-700">{rec.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            <i className="ri-lightbulb-line text-xl text-teal-600"></i>
          </div>
          <div className="text-sm text-gray-700">
            <strong>Pro Tip:</strong> Focus on high-priority recommendations first for the biggest impact on your Health Score. Small, consistent changes lead to lasting improvements.
          </div>
        </div>
      </div>
    </div>
  );
}
